import { useEffect, useState } from "react";
import { RotateCcw, ClipboardCheck, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { EmptyState } from "../../components/shared/EmptyState";
import { api, formatApiError } from "../../lib/api";

const FIELDS = [
  { key: "qty_ok", label: "OK", tone: "text-deepgreen" },
  { key: "qty_missing", label: "Manquant", tone: "text-red-600" },
  { key: "qty_damaged", label: "Cassé", tone: "text-red-600" },
  { key: "qty_cleaning", label: "Nettoyage", tone: "text-gold" },
  { key: "qty_repair", label: "Réparation", tone: "text-gold" },
];

export default function ReturnsPage() {
  const [returns, setReturns] = useState(null);
  const [forms, setForms] = useState({});
  const [notes, setNotes] = useState({});
  const [sending, setSending] = useState(null);

  const load = () => api.get("/admin/logistics/returns")
    .then((r) => {
      setReturns(r.data);
      const init = {};
      r.data.forEach((ret) => {
        init[ret.id] = {};
        ret.items.forEach((it) => {
          init[ret.id][it.product_id] = { qty_ok: it.qty - it.returned_qty, qty_missing: 0, qty_damaged: 0, qty_cleaning: 0, qty_repair: 0 };
        });
      });
      setForms(init);
    })
    .catch(() => toast.error("Retours indisponibles"));
  useEffect(() => { load(); }, []);

  const setVal = (rid, pid, key, value) =>
    setForms((prev) => ({ ...prev, [rid]: { ...prev[rid], [pid]: { ...prev[rid][pid], [key]: Math.max(0, Number(value) || 0) } } }));

  const submit = async (ret) => {
    setSending(ret.id);
    try {
      const items = Object.entries(forms[ret.id]).map(([product_id, v]) => ({ product_id, ...v }));
      await api.post(`/admin/logistics/reservations/${ret.id}/return-inspection`, { items, notes: notes[ret.id] || null });
      toast.success(`Contrôle enregistré pour ${ret.ref} — disponibilités mises à jour`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(null); }
  };

  if (!returns) return <div className="py-16 text-center font-heading text-deepgreen" data-testid="returns-loading">Chargement…</div>;

  return (
    <div data-testid="admin-returns-page">
      <p className="overline-ae">B09 — Retours &amp; état du matériel</p>
      <h1 className="mt-1 font-heading text-2xl sm:text-3xl text-deepgreen">Retours &amp; contrôle du matériel</h1>
      <p className="mt-1 text-sm text-muted-foreground">
        Contrôle par quantité, retours partiels acceptés. Le matériel ne redevient disponible qu'après ce contrôle —
        jamais automatiquement à la date théorique. Casse, manquant, nettoyage et réparation restent indisponibles jusqu'à décision Art Event.
      </p>

      {returns.length === 0 ? (
        <div className="mt-6">
          <EmptyState icon={RotateCcw} title="Aucun retour à contrôler">Les réservations confirmées dont la date de fin approche ou est passée apparaîtront ici.</EmptyState>
        </div>
      ) : (
        <div className="mt-6 space-y-6">
          {returns.map((ret) => (
            <div key={ret.id} className="card-ae p-5" data-testid={`return-card-${ret.ref}`}>
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                  <p className="font-heading text-lg text-deepgreen">{ret.ref} — {ret.client}</p>
                  <p className="text-xs text-muted-foreground">Fin de location : {ret.end_date} — statut logistique : {ret.delivery_status}</p>
                </div>
                {ret.late && (
                  <span className="flex items-center gap-2 rounded-full bg-red-100 px-3 py-1 text-xs font-bold text-red-700" data-testid={`return-late-${ret.ref}`}>
                    <AlertTriangle className="h-3.5 w-3.5" /> Retour en retard
                  </span>
                )}
              </div>
              <div className="mt-4 space-y-4">
                {ret.items.map((it) => {
                  const expected = it.qty - it.returned_qty;
                  const f = forms[ret.id]?.[it.product_id] || {};
                  return (
                    <div key={it.product_id} className="rounded-lg bg-cream p-4" data-testid={`return-item-${ret.ref}-${it.product_id}`}>
                      <p className="text-sm font-semibold text-deepgreen">
                        {it.name} <span className="ml-2 text-xs font-normal text-muted-foreground">attendu : {expected} (loué {it.qty}, déjà retourné {it.returned_qty})</span>
                      </p>
                      <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-5">
                        {FIELDS.map((field) => (
                          <div key={field.key}>
                            <label className={`mb-1 block text-[11px] font-bold uppercase tracking-wider ${field.tone}`}>{field.label}</label>
                            <input
                              type="number" min="0" max={expected}
                              className="input-ae !py-2"
                              value={f[field.key] ?? 0}
                              onChange={(e) => setVal(ret.id, it.product_id, field.key, e.target.value)}
                              data-testid={`return-${field.key}-${ret.ref}-${it.product_id}`}
                            />
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4 flex flex-wrap items-center gap-3">
                <input className="input-ae flex-1 min-w-52" placeholder="Notes du contrôle (facultatif)" value={notes[ret.id] || ""} onChange={(e) => setNotes((prev) => ({ ...prev, [ret.id]: e.target.value }))} data-testid={`return-notes-${ret.ref}`} />
                <button type="button" onClick={() => submit(ret)} disabled={sending === ret.id} className="btn-primary-ae !px-5 !py-2.5 !text-xs disabled:opacity-50" data-testid={`return-submit-${ret.ref}`}>
                  <ClipboardCheck className="h-4 w-4" /> {sending === ret.id ? "Enregistrement…" : "Enregistrer le contrôle"}
                </button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Contrôle partiel possible : seules les quantités déclarées sont enregistrées, le reste reste attendu.</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
